const DEFAULT_KEEPALIVE_INTERVAL_MS = 1800000;
const MIN_KEEPALIVE_INTERVAL_MS = 300000;
const MAX_KEEPALIVE_INTERVAL_MS = 1800000;

function clampInteger(value, fallback, min, max) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.max(min, Math.min(max, Math.trunc(number)));
}

function defaultScheduler() {
  return {
    setTimeout: (callback, delay) => globalThis.setTimeout(callback, delay),
    clearTimeout: (id) => globalThis.clearTimeout(id),
  };
}

function defaultVisibility() {
  return globalThis.document || {
    hidden: false,
    addEventListener() {},
    removeEventListener() {},
  };
}

function policyIntervalMs(policy) {
  const seconds = clampInteger(
    policy?.keepalive_recommended_seconds,
    DEFAULT_KEEPALIVE_INTERVAL_MS / 1000,
    MIN_KEEPALIVE_INTERVAL_MS / 1000,
    MAX_KEEPALIVE_INTERVAL_MS / 1000,
  );
  return seconds * 1000;
}

export class SessionSupervisor {
  constructor({
    api = null,
    telemetry = null,
    onAuthenticated = () => {},
    onAuthenticationLost = () => {},
    onError = () => {},
    scheduler = defaultScheduler(),
    visibility = defaultVisibility(),
    intervalMs = DEFAULT_KEEPALIVE_INTERVAL_MS,
  } = {}) {
    this.api = api;
    this.telemetry = telemetry;
    this.onAuthenticated = onAuthenticated;
    this.onAuthenticationLost = onAuthenticationLost;
    this.onError = onError;
    this.scheduler = scheduler;
    this.visibility = visibility;
    this.intervalMs = clampInteger(intervalMs, DEFAULT_KEEPALIVE_INTERVAL_MS, MIN_KEEPALIVE_INTERVAL_MS, MAX_KEEPALIVE_INTERVAL_MS);
    this.running = false;
    this.timer = null;
    this.inFlight = null;
    this.failures = 0;
    this.anonymousFailures = 0;
    this.boundVisibility = () => this.visibilityChanged();
  }

  start(policy = null) {
    this.intervalMs = policyIntervalMs(policy) || this.intervalMs;
    if (this.running) {
      this.schedule(this.intervalMs);
      return;
    }
    this.running = true;
    this.failures = 0;
    this.anonymousFailures = 0;
    this.visibility?.addEventListener?.('visibilitychange', this.boundVisibility);
    this.schedule(this.intervalMs);
  }

  stop() {
    this.running = false;
    this.cancelTimer();
    this.visibility?.removeEventListener?.('visibilitychange', this.boundVisibility);
    this.inFlight = null;
    this.failures = 0;
    this.anonymousFailures = 0;
  }

  async checkNow() {
    if (!this.running || this.inFlight || !this.api?.call) return;
    this.inFlight = this.performCheck().finally(() => { this.inFlight = null; });
    return this.inFlight;
  }

  async performCheck() {
    try {
      const response = await this.api.call('auth.session.get', {}, { dedupeKey: 'auth.session.keepalive' });
      if (!this.running) return;
      if (!response?.authenticated) {
        const reason = String(response?.session_status?.reason || 'anonymous');
        this.anonymousFailures += 1;
        this.record('session.keepalive.unauthenticated', 'warning', 'Session keepalive returned anonymous state', { code: reason, failures: this.anonymousFailures });
        if (['session_expired', 'session_revoked', 'invalid_session', 'user_blocked'].includes(reason) || this.anonymousFailures >= 2) {
          this.stop();
          this.onAuthenticationLost(new Error('Сессия завершена. Войдите снова.'));
          return;
        }
        this.schedule(Math.min(MIN_KEEPALIVE_INTERVAL_MS, this.intervalMs));
        return;
      }
      this.anonymousFailures = 0;
      if (response?.csrf_token) this.api.setCsrfToken(response.csrf_token);
      this.failures = 0;
      this.intervalMs = policyIntervalMs(response?.session_policy) || this.intervalMs;
      this.onAuthenticated(response);
      this.schedule(this.intervalMs);
    } catch (error) {
      if (!this.running) return;
      this.failures += 1;
      const authLost = Number(error?.status || 0) === 401 || Number(error?.status || 0) === 403
        || ['authentication_required', 'unauthorized', 'invalid_session', 'session_idle_timeout', 'session_revoked', 'user_blocked'].includes(String(error?.code || ''));
      // A single keepalive miss that the backoff+retry below immediately recovers from is a
      // transient client connectivity blip, not a background runtime fault - don't record it
      // (it otherwise trips telemetry.background_runtime on every Wi-Fi/sleep/deploy hiccup).
      // A real outage keeps failing across checks and is recorded from the 2nd miss onward;
      // an auth-loss signal is always recorded so the lifecycle stays auditable.
      if (this.failures >= 2 || authLost) {
        this.record('session.keepalive.error', 'warning', error?.message || 'Session keepalive failed', {
          code: String(error?.code || error?.name || 'request_failed'),
          status: Number(error?.status || 0),
          failures: this.failures,
        });
      }
      if (authLost) {
        this.stop();
        this.onAuthenticationLost(error);
        return;
      }
      const backoff = Math.min(MAX_KEEPALIVE_INTERVAL_MS, this.intervalMs * (2 ** Math.min(4, this.failures)));
      this.schedule(backoff);
    }
  }

  visibilityChanged() {
    if (!this.running) return;
    this.schedule(this.intervalMs);
  }

  schedule(delay) {
    if (!this.running) return;
    this.cancelTimer();
    this.timer = this.scheduler.setTimeout(() => {
      this.timer = null;
      void this.checkNow();
    }, Math.max(0, clampInteger(delay, this.intervalMs, 0, MAX_KEEPALIVE_INTERVAL_MS)));
  }

  cancelTimer() {
    if (this.timer === null) return;
    this.scheduler.clearTimeout(this.timer);
    this.timer = null;
  }

  record(eventType, level, message, payload = {}) {
    try {
      this.telemetry?.record?.(eventType, {
        level,
        action: 'auth.session.get',
        message,
        payload: { category: 'session_lifecycle', ...payload },
      });
    } catch {}
  }
}
